import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { PropertyDetail, UpdatePropertyInput } from "./types";
import { PROPERTIES_LIST_QUERY_KEY, PROPERTY_DETAIL_QUERY_KEY } from "./constants";

/**
 * `PATCH /api/admin/properties/:id` — capacity-pivot admin edit. Returns the
 * full admin detail (same shape as the GET). Hand-written `httpClient` call —
 * see CLAUDE.md → TD-07.
 *
 * Access is gated by the API `@Roles(WANDERFIT_ADMIN, WANDERFIT_SUPPORT)` — both
 * roles may edit the full property (no per-field RBAC). FINANCE never reaches
 * this path (the detail-page Edit button hides it).
 */
async function updateProperty(id: string, input: UpdatePropertyInput): Promise<PropertyDetail> {
  return httpClient<PropertyDetail>(`/api/admin/properties/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useUpdateProperty(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePropertyInput) => updateProperty(id, input),
    onSuccess: (detail) => {
      // Seed the detail cache with the fresh record so the page reflects the
      // edit immediately, then invalidate so a concurrent edit (another tab /
      // operator) within the staleTime window still re-fetches the truth. The
      // seed first means no loading flash — it revalidates in the background.
      queryClient.setQueryData([...PROPERTY_DETAIL_QUERY_KEY, id] as const, detail);
      queryClient.invalidateQueries({
        queryKey: [...PROPERTY_DETAIL_QUERY_KEY, id],
      });
      queryClient.invalidateQueries({ queryKey: PROPERTIES_LIST_QUERY_KEY });
    },
  });
}
